export const BRAND_LOGO_COLLECTION = "systemConfig";
export const BRAND_LOGO_DOC_ID = "brandLogo";
export const FALLBACK_EMAIL_LOGO_URL = "https://pos.nuagepos.com/logo.png";

const PUBLIC_HOST = "https://pos.nuagepos.com";

const SAVED_LOGO_SRC = /https:\/\/pos\.nuagepos\.com\/logo(?:\.png|-series\/[^"'<\s]+)|https:\/\/firebasestorage\.googleapis\.com\/v0\/b\/[^"'<\s]*logo-series%2F[^"'<\s]+/g;

export type LogoForm = "long" | "square";

export interface LogoFile {
  seriesId: string;
  seriesLabel: string;
  form: LogoForm;
  width: number;
  height: number;
  fileName: string;
  publicUrl: string;
  previewSrc: string;
}

interface LogoSeries {
  id: string;
  label: string;
  long: LogoFile[];
  square: LogoFile[];
}

const SERIES = [
  { id: "bright_blue", label: "Bright blue" },
  { id: "charcoal", label: "Charcoal" },
  { id: "red", label: "Red" },
  { id: "silver_blue", label: "Silver blue" },
] as const;

const LONG_SIZES: Array<[number, number]> = [
  [1024, 256],
  [512, 128],
  [256, 64],
  [128, 32],
  [64, 16],
  [32, 8],
];

const SQUARE_SIZES = [256, 128, 64, 48, 32, 24, 16];

function logoFile(
  seriesId: string,
  seriesLabel: string,
  form: LogoForm,
  width: number,
  height: number
): LogoFile {
  const fileName =
    form === "square"
      ? `NuagePOS_${seriesId}_square_${width}x${height}.png`
      : `NuagePOS_${seriesId}_${width}x${height}.png`;
  const objectPath = `logo-series/${seriesId}/${fileName}`;
  return {
    seriesId,
    seriesLabel,
    form,
    width,
    height,
    fileName,
    publicUrl: `${PUBLIC_HOST}/logo-series/${seriesId}/${fileName}?v=1`,
    previewSrc: `/${objectPath}`,
  };
}

export const LOGO_SERIES: LogoSeries[] = SERIES.map((series) => ({
  id: series.id,
  label: series.label,
  long: LONG_SIZES.map(([width, height]) => logoFile(series.id, series.label, "long", width, height)),
  square: SQUARE_SIZES.map((size) => logoFile(series.id, series.label, "square", size, size)),
}));

export function applyEmailLogo(html: string, url: string): string {
  const next = String(url || "").trim();
  if (!next.startsWith("https://")) return html;
  return String(html || "").replace(SAVED_LOGO_SRC, next);
}

export function previewSrcForUrl(url: string): string {
  const decoded = decodeURIComponent(String(url || ""));
  const marker = "logo-series/";
  const index = decoded.indexOf(marker);
  if (index >= 0) {
    const rest = decoded.slice(index + marker.length).split("?")[0];
    return `/logo-series/${rest}`;
  }
  return url;
}

export function logoPreviewSize(file: LogoFile): { width: number; height: number } {
  const scale = Math.min(280 / file.width, 72 / file.height, 4);
  const boost = Math.max(scale, 17 / Math.min(file.width, file.height));
  return {
    width: Math.round(file.width * boost),
    height: Math.round(file.height * boost),
  };
}
